import React, { useState, useEffect, useRef } from 'react';
import s from './NovaTicker.module.sass';

// Топ-30 монет по капитализации, обновление раз в 5 минут без перезагрузки.
// CoinGecko — публичный API без ключа (тот же рейтинг, что на CoinMarketCap).
const API_URL = 'https://api.coingecko.com/api/v3/coins/markets'
    + '?vs_currency=usd&order=market_cap_desc&per_page=30&page=1'
    + '&sparkline=false&price_change_percentage=24h';

const REFRESH_MS = 5 * 60 * 1000;
const LOOP_MS = 90 * 1000; // длительность одного круга ленты (синхронно с sass)

// Брендовый запуск (лаунчер выставляет REACT_APP_BRANDED=1). В обычном dev/проде его нет.
const BRANDED = process.env.REACT_APP_BRANDED === '1';

// «Наши» монеты — их в брендовом запуске показываем чуть выше рынка (как в трейдинге).
const OUR_COINS = new Set(['BTC', 'SOL', 'BNB', 'ETH']);
// Долларовые стейблкоины — цену не трогаем, должна оставаться 1:1.
const STABLE_COINS = new Set(['USDT', 'USDC', 'DAI', 'BUSD', 'TUSD', 'USDD', 'FDUSD', 'USDE', 'PYUSD', 'USDP', 'GUSD']);

// Наценка цены только для брендового запуска:
//  • наши монеты (BTC/SOL/BNB/ETH) — чуть выше (+2%)
//  • долларовые стейблкоины — без изменений (1:1)
//  • остальные — примерно +1%
const brandedPrice = (price, symbol) => {
    if (!BRANDED || price == null || isNaN(price)) return price;
    const sym = (symbol || '').toUpperCase();
    // Стейблкоин — по символу или по цене около $1 (в пределах ±2%)
    if (STABLE_COINS.has(sym) || (price >= 0.98 && price <= 1.02)) return price;
    if (OUR_COINS.has(sym)) return price * 1.02;
    return price * 1.01;
};

// Кэш на уровне модуля: данные и фаза анимации переживают перемонтирование
// хедера (например, при переходе в лениво загружаемый раздел),
// поэтому лента не начинается сначала и не дёргает API лишний раз.
const cache = {
    coins: [],
    fetchedAt: 0,
    animStart: Date.now(),
    pausedAt: 0,
};

const formatPrice = (p) => {
    if (p == null || isNaN(p)) return '—';
    if (p >= 1000) return p.toLocaleString('en-US', { maximumFractionDigits: 0 });
    if (p >= 1) return p.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return p.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
};

export default function NovaTicker() {
    const [coins, setCoins] = useState(cache.coins);
    const moverRef = useRef(null);

    // Движение через rAF от общих «часов» модуля: позиция в каждый кадр
    // выводится из одного и того же momentа старта, поэтому рендеры
    // и перемонтирования не сдвигают фазу вообще.
    useEffect(() => {
        const el = moverRef.current;
        if (!el || !coins.length) return undefined;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

        let raf;
        const tick = () => {
            if (!cache.pausedAt) {
                const elapsed = Date.now() - cache.animStart;
                const pct = (elapsed % LOOP_MS) / LOOP_MS * 50;
                el.style.transform = `translate3d(-${pct}%, 0, 0)`;
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [coins.length]);

    // Пауза, поставленная клавишей N (бренд-запуск): её ховер снимать не должен.
    const keyPausedRef = useRef(false);

    // Пауза на ховер: «часы» сдвигаются на время паузы, фаза не рвётся
    const pause = () => { if (!cache.pausedAt) cache.pausedAt = Date.now(); };
    const resume = () => {
        // Не снимаем паузу, если она удерживается клавишей N
        if (keyPausedRef.current) return;
        if (cache.pausedAt) {
            cache.animStart += Date.now() - cache.pausedAt;
            cache.pausedAt = 0;
        }
    };

    // Бренд-запуск: клавиша N ставит бегущую строку на паузу / снимает её,
    // продолжая ровно с того же места (фаза «часов» сохраняется).
    useEffect(() => {
        if (!BRANDED) return undefined;
        const onKey = (e) => {
            if (e.key !== 'n' && e.key !== 'N' && e.code !== 'KeyN') return;
            // Не перехватываем, если пользователь печатает в поле ввода
            const el = e.target;
            const tag = (el?.tagName || '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) return;
            if (keyPausedRef.current) {
                // Снимаем паузу: сдвигаем старт на длительность стоянки
                keyPausedRef.current = false;
                if (cache.pausedAt) {
                    cache.animStart += Date.now() - cache.pausedAt;
                    cache.pausedAt = 0;
                }
            } else {
                keyPausedRef.current = true;
                if (!cache.pausedAt) cache.pausedAt = Date.now();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        let alive = true;
        let interval = null;

        const load = async () => {
            try {
                const res = await fetch(API_URL);
                if (!res.ok) return;
                const data = await res.json();
                if (Array.isArray(data) && data.length) {
                    cache.coins = data;
                    cache.fetchedAt = Date.now();
                    if (alive) setCoins(data);
                }
            } catch (_e) { /* сеть недоступна — оставляем прошлые данные */ }
        };

        // Если кэш свежий — не дёргаем API, просто ждём до следующего окна
        const sinceLast = Date.now() - cache.fetchedAt;
        if (!cache.coins.length || sinceLast >= REFRESH_MS) {
            load();
        }
        const timeout = setTimeout(() => {
            load();
            interval = setInterval(load, REFRESH_MS);
        }, Math.max(1000, REFRESH_MS - sinceLast));

        return () => {
            alive = false;
            clearTimeout(timeout);
            if (interval) clearInterval(interval);
        };
    }, []);

    if (!coins.length) return <div className={s.ticker} aria-hidden="true" />;

    const items = coins.map((c) => {
        const change = c.price_change_percentage_24h;
        const positive = change == null ? null : change >= 0;
        return {
            id: c.id,
            symbol: (c.symbol || '').toUpperCase(),
            icon: c.image || null,
            price: formatPrice(brandedPrice(c.current_price, c.symbol)),
            change: change == null ? '' : `${positive ? '↑' : '↓'}${Math.abs(change).toFixed(2)}%`,
            positive,
        };
    });

    const renderList = (ariaHidden) => (
        <ul className={s.track} aria-hidden={ariaHidden}>
            {items.map((it) => (
                <li key={`${ariaHidden ? 'b' : 'a'}-${it.id}`} className={s.item}>
                    {it.icon && <img src={it.icon} alt="" className={s.icon} loading="lazy" />}
                    <span className={s.pair}>
                        <span className={s.symbol}>{it.symbol}</span>
                        <span className={s.quote}>/USD</span>
                    </span>
                    <span className={s.price}>{it.price}</span>
                    {it.change && (
                        <span className={`${s.change} ${it.positive ? s.up : s.down}`}>{it.change}</span>
                    )}
                </li>
            ))}
        </ul>
    );

    // Стартовая позиция для первого кадра (до первого тика rAF)
    const elapsedNow = (cache.pausedAt || Date.now()) - cache.animStart;
    const initialPct = (elapsedNow % LOOP_MS) / LOOP_MS * 50;

    return (
        <div className={s.ticker} onMouseEnter={pause} onMouseLeave={resume}>
            <div
                ref={moverRef}
                className={s.mover}
                data-nova-ticker-mover="true"
                style={{ transform: `translate3d(-${initialPct}%, 0, 0)` }}
            >
                {renderList(false)}
                {renderList(true)}
            </div>
        </div>
    );
}
